import React, { useState, useMemo, useRef, useEffect } from "react";
import { Input } from "./input";
import { Button } from "./button";
import { Search, Plus, Check, X } from "lucide-react";

/**
 * SearchablePicker
 * - Lista pesquisavel com input de busca em destaque
 * - Botao "+ Cadastrar novo" opcional
 * - Itens: [{ value, label, sublabel? }]
 *
 * Props:
 *  - value, onChange(value)
 *  - options: array
 *  - placeholder
 *  - emptyText
 *  - onCreateNew?: () => void  (se passado, mostra botao "+ Cadastrar novo")
 *  - createNewLabel?: string
 *  - testId?: string
 */
export function SearchablePicker({
  value,
  onChange,
  options = [],
  placeholder = "Selecione...",
  emptyText = "Nenhum resultado",
  onCreateNew,
  createNewLabel = "+ Cadastrar novo",
  testId,
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const wrapRef = useRef(null);

  useEffect(() => {
    const onClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const selected = useMemo(
    () => options.find((o) => o.value === value),
    [options, value]
  );

  const filtered = useMemo(() => {
    const q = (query || "").toLowerCase().trim();
    if (!q) return options;
    return options.filter((o) =>
      (o.label || "").toLowerCase().includes(q) ||
      (o.sublabel || "").toLowerCase().includes(q)
    );
  }, [options, query]);

  return (
    <div ref={wrapRef} className="relative" data-testid={testId}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={`w-full flex items-center justify-between px-3 py-2 rounded-md border bg-white text-left text-sm transition ${
          open ? "border-luvcorp-navy ring-1 ring-luvcorp-navy/30" : "border-slate-300 hover:border-slate-400"
        }`}
      >
        <span className={selected ? "text-slate-900" : "text-slate-400"}>
          {selected ? selected.label : placeholder}
        </span>
        <Search className="h-3.5 w-3.5 text-slate-400 ml-2 flex-shrink-0" />
      </button>

      {open && (
        <div className="absolute z-50 mt-1 w-full bg-white border border-slate-200 rounded-md shadow-lg max-h-72 overflow-hidden flex flex-col">
          <div className="p-2 border-b border-slate-100 sticky top-0 bg-white">
            <div className="relative">
              <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
              <Input
                autoFocus
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Buscar por nome..."
                className="pl-7 h-8 text-sm"
              />
            </div>
          </div>

          <div className="flex-1 overflow-y-auto py-1">
            {value && (
              <button
                type="button"
                onClick={() => { onChange(""); setOpen(false); setQuery(""); }}
                className="w-full text-left px-3 py-1.5 text-xs text-slate-500 hover:bg-slate-50 italic"
              >
                — Limpar seleção —
              </button>
            )}

            {filtered.length === 0 && (
              <p className="px-3 py-3 text-sm text-slate-500 text-center">{emptyText}</p>
            )}

            {filtered.map((o) => {
              const ativo = o.value === value;
              return (
                <button
                  key={o.value || "__empty"}
                  type="button"
                  onClick={() => { onChange(o.value); setOpen(false); setQuery(""); }}
                  className={`w-full flex items-center gap-2 px-3 py-1.5 text-sm text-left hover:bg-luvcorp-navy/5 ${
                    ativo ? "bg-luvcorp-navy/10 font-medium" : ""
                  }`}
                >
                  <span className="flex-1 truncate">
                    {o.label}
                    {o.sublabel && (
                      <span className="ml-2 text-[10px] text-slate-400">{o.sublabel}</span>
                    )}
                  </span>
                  {ativo && <Check className="h-3.5 w-3.5 text-luvcorp-navy" />}
                </button>
              );
            })}
          </div>

          {onCreateNew && (
            <div className="border-t border-slate-100 p-2 sticky bottom-0 bg-white">
              <Button
                type="button"
                onClick={() => { setOpen(false); onCreateNew(query); setQuery(""); }}
                variant="outline"
                className="w-full justify-center text-sm h-8"
                data-testid={`${testId || "picker"}-create-new`}
              >
                <Plus className="h-3.5 w-3.5 mr-1.5" />
                {createNewLabel}
                {query && <span className="ml-2 text-xs text-slate-500">"{query}"</span>}
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default SearchablePicker;
